// Accessibility utilities to prevent focus management issues

/**
 * Manages focus states for modals and overlays to prevent aria-hidden accessibility issues
 */
export class FocusManager {
  private static activeElement: Element | null = null;
  private static managedElements: Set<HTMLElement> = new Set();

  /**
   * Makes an element inert (prevents focus and interaction) instead of using aria-hidden
   * This prevents the accessibility warning about aria-hidden blocking focus
   */
  static makeInert(element: HTMLElement): void {
    // Skip if this element contains the Lovable chat interface
    const hasLovableChat = element.querySelector('#chatinput') || 
                          element.querySelector('[data-lovable-chat]') ||
                          element.closest('[data-lovable-chat]') ||
                          element.id === 'chatinput';
    
    if (hasLovableChat) {
      console.log('🔒 Skipping inert on element containing Lovable chat interface');
      return;
    }

    // Prefer inert attribute over aria-hidden to prevent focus issues
    if ('inert' in element && typeof (element as any).inert === 'boolean') {
      (element as any).inert = true;
      this.managedElements.add(element);
    } else {
      // Fallback for browsers that don't support inert
      // Use visibility instead of aria-hidden for better accessibility
      element.style.visibility = 'hidden';
      element.style.pointerEvents = 'none';
      this.managedElements.add(element);
    }
  }

  /**
   * Removes inert state from an element
   */
  static removeInert(element: HTMLElement): void {
    if ('inert' in element && typeof (element as any).inert === 'boolean') {
      (element as any).inert = false;
    } else {
      // Fallback cleanup
      element.removeAttribute('aria-hidden');
      element.style.visibility = '';
      element.style.pointerEvents = '';
    }
    this.managedElements.delete(element);
  }

  /**
   * Traps focus within a container (for modals, dialogs)
   */
  static trapFocus(container: HTMLElement): () => void {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement?.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement?.focus();
          e.preventDefault();
        }
      }
    };

    container.addEventListener('keydown', handleTab);
    
    // Return cleanup function
    return () => {
      container.removeEventListener('keydown', handleTab);
    };
  }

  /**
   * Store current focus to restore later
   */
  static storeFocus(): void {
    this.activeElement = document.activeElement;
  }

  /**
   * Restore previously stored focus
   */
  static restoreFocus(): void {
    if (this.activeElement && this.activeElement instanceof HTMLElement) {
      this.activeElement.focus();
      this.activeElement = null;
    }
  }

  /**
   * Clean up all managed elements
   */
  static cleanup(): void {
    this.managedElements.forEach(element => {
      this.removeInert(element);
    });
    this.managedElements.clear();
  }
}

/**
 * React hook for managing modal accessibility
 */
export function useModalAccessibility(isOpen: boolean) {
  const setupModal = (container: HTMLElement) => {
    if (!isOpen) return;

    // Store current focus
    FocusManager.storeFocus();

    // Make body content inert, but exclude Lovable chat interface
    const mainContent = document.querySelector('main');
    if (mainContent instanceof HTMLElement) {
      // Check if this is the Lovable chat interface area
      const isLovableChatArea = container.closest('[data-lovable-chat]') || 
                               container.querySelector('#chatinput') ||
                               document.querySelector('#chatinput');
      
      // Only make content inert if it's not related to the Lovable chat
      if (!isLovableChatArea) {
        FocusManager.makeInert(mainContent);
      }
    }

    // Trap focus in modal
    const cleanup = FocusManager.trapFocus(container);

    return () => {
      cleanup();
      // Only remove inert if we applied it
      const isLovableChatArea = container.closest('[data-lovable-chat]') || 
                               container.querySelector('#chatinput') ||
                               document.querySelector('#chatinput');
      
      if (mainContent instanceof HTMLElement && !isLovableChatArea) {
        FocusManager.removeInert(mainContent);
      }
      FocusManager.restoreFocus();
    };
  };

  return { setupModal };
}

/**
 * Helper to check if browser supports inert attribute
 */
export function supportsInert(): boolean {
  return 'inert' in HTMLElement.prototype;
}

/**
 * Polyfill for inert attribute if needed
 * This can be called during app initialization
 */
export function ensureInertSupport(): void {
  if (!supportsInert()) {
    // Add a note for developers to include inert polyfill
    console.warn(
      'Browser does not support inert attribute. Consider adding a polyfill: npm install wicg-inert'
    );
  }
}